import { apiBaseUrl } from '@/platform/config'

// On native (Capacitor) the WebView origin is `https://localhost`, so a bare
// `/api/v1/...` path 404s against the WebView itself. `apiBaseUrl()` resolves
// to `VITE_API_BASE_URL` on native and to '' on web (same-origin Rails).
const BASE = `${apiBaseUrl()}/api/v1`

async function request(url: string, options: RequestInit = {}) {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  })
  const json = await res.json()
  if (!json.success) throw new Error(json.error || 'Request failed')
  return json.data
}

function authHeaders(token: string) {
  return { 'Authorization': `Bearer ${token}` }
}

// Registration
export const register = (data: {
  email: string
  password: string
  password_confirmation: string
  first_name: string
  last_name: string
  referral_code?: string
}) =>
  request(`${BASE}/auth/register`, {
    method: 'POST',
    body: JSON.stringify({ user: data }),
  })

// Login
export const login = (email: string, password: string) =>
  request(`${BASE}/auth/login`, {
    method: 'POST',
    body: JSON.stringify({ user: { email, password } }),
  })

// Forgot Password
export const forgotPassword = (email: string) =>
  request(`${BASE}/auth/forgot_password`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  })

// Reset Password
export const resetPassword = (token: string, password: string, passwordConfirmation: string) =>
  request(`${BASE}/auth/reset_password`, {
    method: 'POST',
    body: JSON.stringify({ token, password, password_confirmation: passwordConfirmation }),
  })

// Verify Email
export const verifyEmail = (token: string) =>
  request(`${BASE}/auth/verify_email`, {
    method: 'POST',
    body: JSON.stringify({ token }),
  })

// Resend Verification
export const resendVerification = (token: string) =>
  request(`${BASE}/auth/resend_verification`, {
    method: 'POST',
    headers: authHeaders(token),
  })

// Onboarding Step
export const updateOnboarding = (token: string, data: Record<string, unknown>) =>
  request(`${BASE}/auth/onboarding`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(data),
  })

// Complete Onboarding
export const completeOnboarding = (token: string) =>
  request(`${BASE}/auth/complete_onboarding`, {
    method: 'POST',
    headers: authHeaders(token),
  })

// Get Current User
export const getMe = (token: string) =>
  request(`${BASE}/auth/me`, {
    headers: authHeaders(token),
  })

// Refresh Token
export const refreshToken = (rawRefreshToken: string) =>
  request(`${BASE}/auth/refresh`, {
    method: 'POST',
    body: JSON.stringify({ refresh_token: rawRefreshToken }),
  })
